import { useEffect, useMemo, useState } from "react";
import { useNavigate, useRouteLoaderData } from "@remix-run/react";
import { BffClient } from "@made-people/gears-bff-client";
import {
  useGearsConfigurationVariable,
  useGearsServiceUrl,
} from "@made-people/gears-configuration";
import { useGreaseGrowthbookFeatureValue } from "@made-people/grease-growthbook";
import { useGtm } from "@made-people/grease-gtm";
import { useHref } from "@made-people/grease-link";
import {
  useProductBase,
  useProductPurchaseInfo,
} from "@made-people/grease-product";
import { useTranslate } from "@made-people/grease-translations";
import { useFrontendContext } from "../../../hooks/use-frontend-context";
import { useGeneralSettings } from "../../../hooks/use-general-settings";
import { mmRootLoader } from "../../../root-loader/mm-root-loader";
import { getStoryblokAssetUrl, parseComfortSuiteSettings } from "../settings";
import { StylePickerSkeleton } from "./style-picker-skeleton";
import { useStylePickerTracking } from "./use-style-picker-tracking";
import { useStyleProducts } from "./use-style-products";
import { cloudflareImageProcessor } from "../../../bootstrap/image-processor";

export const StylePicker = () => {
  const rootLoaderData = useRouteLoaderData<typeof mmRootLoader>("root");
  const hasServerPayload = Boolean(rootLoaderData?.gbState?.p);
  const [mounted, setMounted] = useState(false);
  const translate = useTranslate();
  const navigate = useNavigate();
  const href = useHref();
  const { pushCustomEvent } = useGtm();
  const frontendContext = useFrontendContext();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");
  const bffClient = useMemo(
    () => new BffClient({ baseUrl: bffUrl, tenantId }),
    [bffUrl, tenantId],
  );
  const productBase = useProductBase();
  const purchaseInfo = useProductPurchaseInfo();
  const generalSettings = useGeneralSettings();
  const { stylePickerItems, stylePickerGroups } = useMemo(
    () => parseComfortSuiteSettings(generalSettings),
    [generalSettings],
  );
  const customisationActive = useMemo(() => {
    if (!stylePickerGroups.length) {
      return false;
    }
    return stylePickerGroups.some((group) => {
      if (!group.categoryPage) {
        return false;
      }
      return productBase.categories.some(
        (category) => category.slug === group.categoryPage,
      );
    });
  }, [productBase.categories, stylePickerGroups]);
  const comfortSuiteStylePickerEnabled =
    useGreaseGrowthbookFeatureValue("comfort-suite:style-picker", false) ??
    false;
  const styles = useStyleProducts({
    enabled: comfortSuiteStylePickerEnabled,
    stylePickerItems,
    bffClient,
    frontendContext,
  });
  const { handleSubmit, isActiveStyle } = useStylePickerTracking({
    href,
    navigate,
    pushCustomEvent,
    productBase,
    purchaseInfo,
    stylePickerItems,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!hasServerPayload && !mounted) {
    return null;
  }

  if (!customisationActive) {
    return null;
  }

  if (stylePickerItems.length === 0) {
    return null;
  }

  if (!comfortSuiteStylePickerEnabled) {
    return null;
  }

  if (styles.length === 0) {
    return <StylePickerSkeleton />;
  }

  return (
    <div className="mt-6 flex flex-col gap-3">
      <span className="text-theme-label-sm">
        {translate("Style_Picker_Choose_Style")}
      </span>
      <div className="flex flex-row gap-3">
        {styles.map((style, index) => {
          const isActive = isActiveStyle(style);

          const imageUrl = getStoryblokAssetUrl(style.item.image);
          const processedImageUrl = imageUrl
            ? cloudflareImageProcessor.process(imageUrl, { width: 120 })
            : undefined;

          return (
            <button
              key={index}
              className={`flex flex-1 max-w-[120px] flex-col gap-2 rounded-md p-2 text-black transition-colors ${
                isActive ? "bg-black text-white" : "bg-[#F0ECE4]"
              }`}
              onClick={() => handleSubmit(style)}
            >
              {imageUrl && (
                <img
                  className="aspect-[3/2] w-full rounded-md object-cover"
                  src={processedImageUrl}
                  alt={style.item.image?.alt ?? ""}
                />
              )}
              {style.item.label && (
                <span className="text-body-xs flex items-center justify-center text-center">
                  {style.item.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
